import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { pillarArticles, type ArticleLanguage, type ArticleTranslation } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import JsonLd from '@/components/feature/JsonLd';
import { buildArticleSchema, buildFaqSchema } from '@/lib/schema';

function getContent(article: (typeof pillarArticles)[number], lang: string): ArticleTranslation {
  const key = (lang as ArticleLanguage) in article ? (lang as ArticleLanguage) : 'en';
  return article[key];
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { i18n, t } = useTranslation();

  const article = pillarArticles.find((a) => a.slug === slug);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    if (article) {
      const content = getContent(article, i18n.language);
      document.title = `${content.title} | USFansLink`;
    }
  }, [i18n.language, article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">
            {t('article.notFound')}
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            {t('nav.home')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const content = getContent(article, i18n.language);
  const related = pillarArticles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const schema = [
    buildArticleSchema({
      path: `/article/${article.slug}`,
      headline: content.title,
      description: content.description,
      image: article.image,
      language: i18n.language,
    }),
    buildFaqSchema(content.faq),
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={schema} />
      <Navbar />
      <main>
        <article className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">
              {t('nav.home')}
            </Link>
            <span>/</span>
            <span className="text-foreground-700">{t('nav.shoppingGuide')}</span>
          </nav>

          <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {content.title}
          </h1>

          <p className="mt-4 text-base leading-relaxed text-foreground-600">{content.description}</p>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {t('article.author')}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {t('article.updated')}
            </span>
          </div>

          <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
            <img
              src={article.image}
              alt={content.title}
              title={`${content.title} — USFans Spreadsheet`}
              className="h-64 w-full object-cover object-top md:h-80"
            />
          </div>

          <div className="mt-8">
            <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{content.intro}</p>
          </div>

          {content.sections.map((section) => (
            <section key={section.heading} className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {section.heading}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                {section.body}
              </p>
            </section>
          ))}

          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t('article.faqTitle')}
            </h2>
            <div className="mt-4 space-y-3">
              {content.faq.map((item) => (
                <div key={item.question} className="rounded-lg border border-background-200 bg-white p-5">
                  <h3 className="text-sm font-bold text-foreground-900 md:text-base">{item.question}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-lg bg-primary-500 p-8 text-center">
            <h2 className="font-heading text-xl font-bold text-white md:text-2xl">
              {t('article.ctaTitle')}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-primary-50 md:text-base">
              {t('article.ctaText')}
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full whitespace-nowrap rounded-md bg-white px-6 py-3 text-sm font-bold text-primary-700 transition-colors hover:bg-primary-50 sm:w-auto"
              >
                {t('article.ctaButton')}
              </a>
              <Link
                to="/usfans-spreadsheet-guide"
                className="w-full whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-accent-600 sm:w-auto"
              >
                {t('nav.shoppingGuide')}
              </Link>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t('article.related')}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {related.map((rel) => {
                const relContent = getContent(rel, i18n.language);
                return (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group overflow-hidden rounded-lg border border-background-200 bg-white transition-colors hover:border-primary-300"
                  >
                    <div className="h-28 w-full overflow-hidden">
                      <img
                        src={rel.image}
                        alt={relContent.title}
                        title={relContent.title}
                        className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <p className="px-3 py-3 text-xs font-semibold leading-snug text-foreground-800">
                      {relContent.title}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}